import React, { useState, useEffect, useRef } from "react";
import { MinusOutlined, PlusOutlined } from "@ant-design/icons";

const NumberOfGuestsFilter = ({ value = { adults: 2, children: 0 }, onChange }) => {
  const [adults, setAdults] = useState(value.adults ?? 2);
  const [children, setChildren] = useState(value.children ?? 0);
  const isFirstRender = useRef(true);

  // ✅ Only sync from parent when value changes externally
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    // Sync only if different
    if (value.adults !== adults || value.children !== children) {
      setAdults(value.adults ?? 2);
      setChildren(value.children ?? 0);
    }
  }, [value]);

  // ✅ Trigger parent callback whenever local changes
  useEffect(() => {
    onChange?.({ adults, children });
  }, [adults, children]);

  const handleAdultChange = (delta) => {
    setAdults((prev) => Math.max(1, prev + delta)); // Min 1 adult
  };

  const handleChildChange = (delta) => {
    setChildren((prev) => Math.max(0, prev + delta)); // Min 0 children
  };

  return (
    <div className="bg-white rounded-xl flex flex-col items-center justify-start p-8 w-full">
      <h2 className="text-xl font-semibold text-[#1a1a1a] mb-10">
        Number of guests
      </h2>

      {/* Counters */}
      <div className="flex flex-wrap justify-center gap-16 mb-6">
        {/* Adults */}
        <div className="flex flex-col items-center">
          <h3 className="text-lg font-semibold text-gray-800 mb-2">Adults</h3>
          <div className="flex items-center gap-6">
            <button
              onClick={() => handleAdultChange(-1)}
              className="w-10 h-10 border border-gray-300 rounded-full flex items-center justify-center text-xl text-gray-700 hover:border-blue-600 hover:text-blue-600"
            >
              <MinusOutlined />
            </button>
            <span className="text-xl font-semibold w-6 text-center">
              {adults}
            </span>
            <button
              onClick={() => handleAdultChange(1)}
              className="w-10 h-10 border border-gray-300 rounded-full flex items-center justify-center text-xl text-gray-700 hover:border-blue-600 hover:text-blue-600"
            >
              <PlusOutlined />
            </button>
          </div>
        </div>

        {/* Children */}
        <div className="flex flex-col items-center">
          <h3 className="text-lg font-semibold text-gray-800 mb-1">Children</h3>
          <p className="text-xs text-gray-500 mb-2">Under 12 years</p>
          <div className="flex items-center gap-6">
            <button
              onClick={() => handleChildChange(-1)}
              className="w-10 h-10 border border-gray-300 rounded-full flex items-center justify-center text-xl text-gray-700 hover:border-blue-600 hover:text-blue-600"
            >
              <MinusOutlined />
            </button>
            <span className="text-xl font-semibold w-6 text-center">
              {children}
            </span>
            <button
              onClick={() => handleChildChange(1)}
              className="w-10 h-10 border border-gray-300 rounded-full flex items-center justify-center text-xl text-gray-700 hover:border-blue-600 hover:text-blue-600"
            >
              <PlusOutlined />
            </button>
          </div>
        </div>
      </div>

      <p className="text-sm text-gray-500 text-center max-w-sm">
        If you have more than 4 guests, you can book multiple staterooms.
      </p>
    </div>
  );
};

export default NumberOfGuestsFilter;
